<template>
	<v-card
		class="d-flex flex-column"
		:class="{ 'pb-5': !nested }"
		:border="nested"
		:loading="submitting"
	>
		<v-toolbar color="primary" dark flat :dense="nested">
			<v-toolbar-title v-if="editMode">
				{{ editTitle }}
			</v-toolbar-title>

			<v-toolbar-title v-else>
				<v-icon v-if="nested" class="mt-n1 mr-1">
					mdi-folder-plus-outline
				</v-icon>

				{{ title }}
			</v-toolbar-title>

			<v-spacer />

			<v-hover-icon v-if="!nested" class="mr-3" />
		</v-toolbar>

		<v-form autocomplete="off" @submit.prevent>
			<v-card-text
				:class="{ 'pa-10': !nested, 'pb-0': !nested, 'px-8': nested }"
			>
				<v-row cols="12">
					<v-col class="pl-0" cols="10">
						<v-text-field
							v-model="name"
							:error-messages="nameErrors"
							:messages="nameMessages"
							:disabled="!!submitting"
							label="Paper name"
							name="name"
							type="text"
							required
							class="col-12"
							@blur="v$.name.$touch()"
						>
							<template #message="{ message }">
								<span
									class="duplicate-inline d-flex align-center ga-2"
									:title="titleForMessage(message)?.name || null"
								>
									<span>{{ message }}</span>

									<a
										v-if="
											titleForMessage(message) &&
											canOfferAttach &&
											!titleForMessage(message).alreadyInProject
										"
										class="duplicate-inline__action"
										href="#"
										@click.prevent="attachExisting(titleForMessage(message))"
									>
										{{
											attaching === titleForMessage(message).paperUuid
												? "Attaching..."
												: "Attach to this project"
										}}
									</a>
								</span>
							</template>
						</v-text-field>
					</v-col>
					<v-col cols="2">
						<v-text-field
							v-model="priority"
							:error-messages="priorityErrors"
							:disabled="!!submitting"
							label="Priority"
							name="priority"
							type="text"
							class="col-12"
							@blur="v$.priority.$touch()"
							@update:model-priority="v$.name.$touch()"
						/>
					</v-col>
				</v-row>

				<v-row>
					<v-text-field
						v-model.trim="url"
						:error-messages="urlErrors"
						:messages="urlMessages"
						:disabled="!!submitting"
						label="Source URL"
						name="url"
						type="url"
						class="col-12"
						@blur="v$.url.$touch()"
					>
						<template #message="{ message }">
							<span class="duplicate-inline d-flex align-center ga-2">
								<span>{{ message }}</span>

								<a
									v-if="message === ctgovImportOffer"
									class="duplicate-inline__action"
									href="#"
									@click.prevent="getCtgovPreview"
								>
									Import from ClinicalTrials.gov
								</a>
							</span>
						</template>
					</v-text-field>
				</v-row>

				<v-row>
					<v-col cols="12" class="pa-0">
						<v-text-field
							v-model="doi"
							:error-messages="doiErrors"
							:messages="doiMessages"
							:disabled="!!submitting"
							label="DOI"
							name="doi"
							@blur="v$.doi.$touch()"
						>
							<template #message="{ message }">
								<span
									class="duplicate-inline d-flex align-center ga-2"
									:title="duplicateForMessage('doi', message)?.name || null"
								>
									<span>{{ message }}</span>

									<template v-if="duplicateForMessage('doi', message)">
										<a
											v-if="
												canOfferAttach &&
												!duplicateForMessage('doi', message).alreadyInProject
											"
											class="duplicate-inline__action"
											href="#"
											@click.prevent="
												attachExisting(duplicateForMessage('doi', message))
											"
										>
											{{
												attaching ===
												duplicateForMessage("doi", message).paperUuid
													? "Attaching..."
													: "Attach to this project"
											}}
										</a>
									</template>
								</span>
							</template>
						</v-text-field>
					</v-col>
				</v-row>

				<v-row>
					<v-col cols="6" class="pa-0 pr-1">
						<v-text-field
							v-model="pmid"
							:error-messages="pmidErrors"
							:messages="pmidMessages"
							:disabled="!!submitting"
							label="PubMed ID"
							name="pmid"
							@blur="v$.pmid.$touch()"
						>
							<template #message="{ message }">
								<span
									class="duplicate-inline d-flex align-center ga-2"
									:title="duplicateForMessage('pmid', message)?.name || null"
								>
									<span>{{ message }}</span>

									<template v-if="duplicateForMessage('pmid', message)">
										<a
											v-if="
												canOfferAttach &&
												!duplicateForMessage('pmid', message).alreadyInProject
											"
											class="duplicate-inline__action"
											href="#"
											@click.prevent="
												attachExisting(duplicateForMessage('pmid', message))
											"
										>
											{{
												attaching ===
												duplicateForMessage("pmid", message).paperUuid
													? "Attaching..."
													: "Attach to this project"
											}}
										</a>
									</template>
								</span>
							</template>
						</v-text-field>
					</v-col>

					<v-col cols="6" class="pa-0 pl-1">
						<v-text-field
							v-model="pmcid"
							:error-messages="pmcidErrors"
							:messages="pmcidMessages"
							:disabled="!!submitting"
							label="PubMed Central ID"
							name="pmcid"
							@blur="v$.pmcid.$touch()"
						>
							<template #message="{ message }">
								<span
									class="duplicate-inline d-flex align-center ga-2"
									:title="duplicateForMessage('pmcid', message)?.name || null"
								>
									<span>{{ message }}</span>

									<template v-if="duplicateForMessage('pmcid', message)">
										<a
											v-if="
												canOfferAttach &&
												!duplicateForMessage('pmcid', message).alreadyInProject
											"
											class="duplicate-inline__action"
											href="#"
											@click.prevent="
												attachExisting(duplicateForMessage('pmcid', message))
											"
										>
											{{
												attaching ===
												duplicateForMessage("pmcid", message).paperUuid
													? "Attaching..."
													: "Attach to this project"
											}}
										</a>
									</template>
								</span>
							</template>
						</v-text-field>
					</v-col>
				</v-row>

				<v-alert
					v-if="nctOutsideMessage"
					type="info"
					variant="tonal"
					density="compact"
					class="mt-2 text-body-2"
				>
					{{ nctOutsideMessage }}
				</v-alert>

				<div
					class="citation-details-toggle d-flex align-center my-2 py-1 px-2 rounded cursor-pointer"
					@click="showCitationDetails = !showCitationDetails"
				>
					<v-divider class="mr-3" />
					<v-icon size="small" class="mr-1" color="primary">
						mdi-book-open-variant
					</v-icon>
					<span class="text-body-2 text-primary text-no-wrap">
						Citation Details
					</span>
					<v-icon size="small" class="ml-1" color="primary">
						{{ showCitationDetails ? "mdi-chevron-up" : "mdi-chevron-down" }}
					</v-icon>
					<v-divider class="ml-3" />
				</div>

				<v-row v-show="showCitationDetails">
					<v-text-field
						v-model="publicationTitle"
						:disabled="!!submitting"
						label="Publication title"
						class="col-12"
						@blur="v$.publicationTitle.$touch()"
					/>
				</v-row>

				<v-row v-show="showCitationDetails">
					<v-text-field
						v-model="shortTitle"
						:disabled="!!submitting"
						label="Short title"
						class="col-12"
						@blur="v$.shortTitle.$touch()"
					/>
				</v-row>

				<v-row v-show="showCitationDetails">
					<v-text-field
						v-model="journalAbbreviation"
						:disabled="!!submitting"
						label="Journal abbreviation"
						class="col-8 mr-1"
						@blur="v$.journalAbbreviation.$touch()"
					/>
					<v-text-field
						v-model="issn"
						:disabled="!!submitting"
						label="ISSN"
						class="col-4 ml-1"
						@blur="v$.issn.$touch()"
					/>
				</v-row>

				<v-row v-show="showCitationDetails">
					<v-text-field
						v-model="volume"
						:disabled="!!submitting"
						label="Volume"
						class="col-4 mr-1"
						@blur="v$.volume.$touch()"
					/>
					<v-text-field
						v-model="issue"
						:disabled="!!submitting"
						label="Issue"
						class="col-4 mx-1"
						@blur="v$.issue.$touch()"
					/>
					<v-text-field
						v-model="pages"
						:disabled="!!submitting"
						label="Pages"
						class="col-4 ml-1"
						@blur="v$.pages.$touch()"
					/>
				</v-row>

				<v-row v-show="showCitationDetails">
					<v-text-field
						v-model="itemType"
						:disabled="!!submitting"
						label="Item type"
						class="col-6 mr-1"
						@blur="v$.itemType.$touch()"
					/>
					<v-text-field
						v-model="publicationDate"
						:disabled="!!submitting"
						label="Publication date"
						class="col-6 ml-1"
						@blur="v$.publicationDate.$touch()"
					/>
				</v-row>

				<v-row v-show="showCitationDetails">
					<v-text-field
						:model-value="creatorsDisplay"
						label="Authors"
						class="col-12"
						readonly
					/>
				</v-row>

				<v-row v-show="showCitationDetails">
					<v-text-field
						:model-value="citationTagsDisplay"
						label="MeSH tags"
						class="col-12"
						readonly
					/>
				</v-row>

				<v-row v-show="showCitationDetails">
					<v-textarea
						v-model="abstractNote"
						:disabled="!!submitting"
						label="Abstract"
						variant="underlined"
						color="secondary"
						rows="3"
						max-rows="6"
						auto-grow
						class="col-12"
						@blur="v$.abstractNote.$touch()"
					/>
				</v-row>

				<v-row>
					<v-col>
						<v-dialog v-model="citationDialog" max-width="700px" persistent>
							<template #activator="{ props }">
								<v-btn
									v-bind="props"
									block
									variant="outlined"
									:class="editMode ? 'mb-8' : 'mb-2'"
									color="primary"
									:disabled="!(pmid || pmcid || doi || url)"
									@click="getCitation"
								>
									<v-icon start> mdi-tag-search </v-icon>

									Fetch Citation
								</v-btn>
							</template>

							<paper-citation-confirm
								:citation="citation"
								:loading="!!submitting"
								@confirm="confirmCitation"
								@cancel="cancelCitation"
							/>
						</v-dialog>

						<v-btn
							v-if="!editMode && ctgovSupport"
							block
							variant="outlined"
							class="mb-8"
							color="primary"
							:disabled="!!submitting"
							@click="getCtgovPreview"
						>
							<v-icon start> mdi-database-import </v-icon>

							Import from ClinicalTrials.gov
						</v-btn>

						<v-dialog v-model="ctgovDialog" max-width="700px" persistent>
							<ctgov-import-confirm
								:preview="ctgovPreview"
								:loading="!!submitting"
								@confirm="confirmCtgovImport"
								@cancel="cancelCtgovImport"
							/>
						</v-dialog>

						<v-dialog
							v-model="orderingWarningDialog"
							max-width="600px"
							transition="fade-transition"
						>
							<v-card>
								<v-toolbar color="primary" dark flat>
									<v-toolbar-title>
										File Removal/Re-order Warning
									</v-toolbar-title>
									<v-hover-icon class="mr-3" />
								</v-toolbar>

								<v-divider />

								<v-card-subtitle
									class="text-center py-4 text-wrap text-black opacity-100"
									color="primary"
								>
									<div class="d-flex align-center ml-1">
										<v-icon class="mr-3" color="red" size="x-large">
											mdi-alert-circle
										</v-icon>
										<div class="text-body-1 text-left">
											This paper has in-progress and/or completed data
											extractions. Removing or re-ordering files may impact
											these extractions and is therefore disallowed.
										</div>
									</div>
									<v-row class="mt-4 mb-0 text-center">
										<v-col class="py-0 ml-4">
											<v-list-item-title class="font-weight-bold">
												Existing ordering
											</v-list-item-title>
										</v-col>
										<v-col class="py-0 mr-4">
											<v-list-item-title class="font-weight-bold">
												New ordering
											</v-list-item-title>
										</v-col>
									</v-row>
									<v-row class="mt-0 mb-6 text-left">
										<v-col class="py-0 ml-4">
											<div
												v-for="(file, index) in initialFiles"
												:key="file.uuid"
												class="py-0 my-0"
											>
												{{ index + 1 }}: {{ file.name }}
											</div>
										</v-col>
										<v-col class="py-0 mr-4">
											<div
												v-for="(file, index) in files"
												:key="file.uuid"
												class="py-0 my-0"
											>
												{{ index + 1 }}: {{ file.name }}
											</div>
										</v-col>
									</v-row>
								</v-card-subtitle>

								<v-divider />

								<v-card-actions class="py-5">
									<v-spacer />

									<v-btn
										class="bg-grey"
										elevation="2"
										@click="closeOrderingWarningDialog"
									>
										Ok
									</v-btn>

									<v-spacer />
								</v-card-actions>
							</v-card>
						</v-dialog>

						<v-switch
							v-model="noIDs"
							color="secondary"
							disabled
							label="This paper has no URL, DOI, PMID, or PMCID"
						/>

						<v-switch
							v-model="newVersion"
							color="secondary"
							disabled
							label="This is a new version of an existing paper in Dactyl"
						/>

						<v-switch
							v-model="dataEntryEnabled"
							color="secondary"
							label="This paper is ready for source data extraction"
							@update:model-value="
								() => $nextTick(() => v$.dataEntryEnabled.$touch())
							"
						/>
					</v-col>
				</v-row>

				<v-divider class="my-5" />

				<v-row v-if="!hideFileUpload">
					<v-col class="pl-0">
						<v-card-subtitle
							class="text-subtitle-2 text-fg-2 mb-2 pl-0 d-flex align-center"
						>
							Paper Files
						</v-card-subtitle>
						<file-upload
							v-model="files"
							:disabled="!!submitting"
							@removed="fileRemoved"
						/>
					</v-col>
				</v-row>

				<v-divider class="my-5" />

				<v-row>
					<v-col class="pl-0">
						<v-card-subtitle
							class="text-subtitle-2 text-fg-2 mb-2 pl-0 d-flex align-center"
						>
							Paper Projects
						</v-card-subtitle>

						<project-select
							ref="projectSelect"
							v-model:dirty="projectDirty"
							v-model:invalid="subInvalid"
							:default-projects="projects"
							:use-session-projects="useSessionProjects"
							:locked-project="lockedProject"
							:omit-creation="true"
						/>
					</v-col>
				</v-row>

				<!-- Therapeutic-area characterization (DCTL-2093). Only for an existing
				     paper (needs a persisted uuid to attach to) and only for users who can
				     manage projects - the same authority as the surfaces this dialog is
				     reached from. Adds/removes persist immediately, independent of Update. -->
				<template v-if="editMode && uuid && canManageProjects">
					<v-divider class="my-5" />

					<v-row>
						<v-col class="pl-0">
							<v-card-subtitle
								class="text-subtitle-2 text-fg-2 mb-2 pl-0 d-flex align-center"
							>
								Therapeutic Area Characterization
							</v-card-subtitle>

							<paper-characterization-editor :paper-uuid="uuid" />
						</v-col>
					</v-row>
				</template>
			</v-card-text>

			<v-card-actions v-if="!noActions">
				<v-spacer />

				<v-btn
					v-if="editMode"
					class="bg-secondary"
					elevation="2"
					type="submit"
					:disabled="
						(!v$.$anyDirty && !projectDirty && !filesChanged) ||
						v$.$invalid ||
						!!submitting
					"
					@click="update"
				>
					Update
				</v-btn>

				<v-btn
					v-else
					class="bg-secondary"
					elevation="2"
					type="submit"
					:disabled="v$.$invalid || !!submitting"
					@click="add"
				>
					Create
				</v-btn>

				<v-btn
					v-if="editMode"
					class="bg-grey"
					elevation="2"
					:disabled="!!submitting"
					@click="clear"
				>
					Revert
				</v-btn>

				<v-btn
					v-else
					:disabled="!!submitting"
					class="bg-grey"
					elevation="2"
					@click="clear"
				>
					Clear
				</v-btn>

				<v-btn
					:disabled="!!submitting"
					elevation="2"
					class="bg-grey"
					@click="cancel"
				>
					Cancel
				</v-btn>

				<v-spacer />
			</v-card-actions>
		</v-form>

		<attach-existing-paper-dialog
			v-if="attachChoice.match"
			v-model="attachChoice.open"
			:paper-name="attachChoice.match.name || 'This paper'"
			:project-name="lockedProject?.name || 'this project'"
			:preview="attachChoice.preview"
			:submitting="attachChoice.submitting"
			@keep="chooseAttach(false)"
			@restart="chooseAttach(true)"
		/>
	</v-card>
</template>

<script>
import _ from "lodash";
import { mapActions, mapState } from "pinia";
import { useMainStore } from "@/store";
import { maxLength, requiredUnless, url } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import { useLDFlag } from "launchdarkly-vue-client-sdk";

import AttachExistingPaperDialog from "@/pages/PaperManager/AttachExistingPaperDialog";
import FileUpload from "@/pages/PaperManager/FileUpload";
import PaperCharacterizationEditor from "@/pages/PaperManager/PaperCharacterizationEditor";
import CtgovImportConfirm from "@/pages/PaperManager/CtgovImportConfirm";
import PaperCitationConfirm from "@/pages/PaperManager/PaperCitationConfirm";
import ProjectSelect from "@/pages/ProjectManager/ProjectSelect";
import VHoverIcon from "@/components/VHoverIcon";

// custom validators
const isFalse = (value) => !value;

// How long to wait after the last keystroke before asking the server whether
// an identifier is already in Dactyl.
const IDENTIFIER_CHECK_DEBOUNCE_MS = 400;

// The identifiers the duplicate check covers, in the order the server ranks
// them, with the label each one is called by in this form. `nctId` has no input
// of its own - it arrives from the ClinicalTrials.gov import - so its result is
// only ever shown in the summary alert.
const IDENTIFIER_FIELDS = [
	{ key: "doi", label: "DOI" },
	{ key: "pmid", label: "PubMed ID" },
	{ key: "pmcid", label: "PubMed Central ID" },
	{ key: "nctId", label: "ClinicalTrials.gov ID" },
];

// The identifiers, and only those, refuse a save. `blockingMatch` tests this
// set rather than assuming every key in the answers map belongs to it, so the
// paper name - which is also checked - cannot become a save gate by accident.
const BLOCKING_KEYS = new Set(IDENTIFIER_FIELDS.map(({ key }) => key));

// The paper name is checked alongside the identifiers and answered under the
// same key in the response map, but it is deliberately NOT one of them: a name
// is a constructed string ("Chakravarthy, 2026; Effects of CT-388, ...") that
// people edit by hand, and two genuinely distinct papers can carry the same
// one. It warns; it never blocks.
const TITLE_KEY = "title";

// Every key the answers map can hold - what the lookup asks about and what it
// stores. Wider than IDENTIFIER_FIELDS on purpose; see BLOCKING_KEYS.
const CHECKED_KEYS = [...IDENTIFIER_FIELDS.map(({ key }) => key), TITLE_KEY];

// The terse line a blocking duplicate puts in the field's own message slot,
// which is also what the slot matches on to find ITS line among the field's
// other messages before appending the attach action to it.
//
// The SAME string for all three identifiers, deliberately. The message renders
// directly beneath a field already labelled "DOI" / "PubMed ID" / "PubMed
// Central ID", so naming the identifier again is redundant - and it is not
// free: spelled out, "A paper with this PubMed Central ID exists in another
// project." measures 352px against the 336px the half-width PMCID column
// gives it, so it wrapped to two lines before the attach action was even
// added, and to four with it. Short, it is 151px and the whole line including
// the action fits in 288px with room to spare. Do not reintroduce the
// identifier here, including for DOI where it would fit; there is a test that
// fails if you do.
//
// Three variants, because the line has to stay true, and it is the ONLY thing
// that says where the existing paper is. A blocking match is visible to the
// caller, and for a non-admin visibility is resolved by joining through
// `paper_project` - so "in another project" is accurate by construction. An
// administrator is unscoped and can therefore match an ORPHANED paper, which
// belongs to no project at all; that one reads "in Dactyl". And when the match
// is already in the project the dialog is pinned to - the reported case, a paper
// added twice to one project - it reads "in this project": the earlier build
// said "Already in another project. (already in Pfizer Obesity)", which
// contradicts itself. The attach action is offered for the first two - attaching
// an orphan to a project is arguably the point of the Orphaned Papers view - and
// not for the third, where there is nothing left to attach.
const DUPLICATE_MESSAGES = {
	inProject: "Already in this project.",
	projected: "Already in another project.",
	orphaned: "Already in Dactyl.",
};

// The same three cases for a paper NAME match, spelled out. This line sits
// under the full-width "Paper name" field, so the width pressure that forced the
// identifier copy to be terse does not apply, and here the words are doing real
// work: a name match is a warning, not a refusal, and "Already in this project."
// under a name field would read as the same hard stop the DOI field gives.
const TITLE_MESSAGES = {
	inProject: "A paper with this name is already in this project.",
	projected: "A paper with this name is already in another project.",
	orphaned: "A paper with this name is already in Dactyl.",
};

// Shown for a paper Dactyl holds but the caller may not see. Non-blocking by
// design: the record belongs to another organization and they have no way to
// reach it, so the only useful thing to say is that it exists.
const OUTSIDE_ORGANIZATIONS_MESSAGE =
	"Already in Dactyl (outside your organizations). You can still add it to your project.";

// citation urls
const entrezURL = "https://eutils.ncbi.nlm.nih.gov/entrez/eutils/esearch.fcgi";
const citationFormat = "zotero";
const citoidURL = `https://en.wikipedia.org/api/rest_v1/data/citation/${citationFormat}/`;

export default {
	name: "PaperEdit",

	components: {
		AttachExistingPaperDialog,
		CtgovImportConfirm,
		FileUpload,
		PaperCharacterizationEditor,
		PaperCitationConfirm,
		ProjectSelect,
		VHoverIcon,
	},

	props: {
		defaultPaper: { type: Object, default: () => null },
		hideFileUpload: { type: Boolean },
		nested: Boolean,
		noActions: Boolean,
		// DCTL-2035/DCTL-2495: add/edit paper from a project context. When set,
		// that project is pinned in the selector (pre-selected, not removable);
		// other projects can still be added or removed.
		lockedProject: { type: Object, default: null },
	},
	emits: ["cancelPaperEdit", "refresh", "update:invalid"],

	setup() {
		// CTDG feature flag: hides the ClinicalTrials.gov import entry point
		// (the backend guards the endpoints with the same flag).
		const ctgovSupport = useLDFlag("sdev2-clinical-trials-support", false);
		return {
			v$: useVuelidate(),
			ctgovSupport,
		};
	},

	data() {
		return {
			files:
				this.defaultPaper && this.defaultPaper.files
					? [...this.defaultPaper.files]
					: [],
			filesChanged: false,
			// The files as last loaded from the server, deep-cloned so the form
			// can be compared against them and reverted to them. Declared here
			// rather than sprung into existence by loadPaper(): in add mode
			// loadPaper never runs the branch that creates it, which left it
			// undefined for the `files` watcher and for the ordering-warning
			// dialog's "Existing ordering" column.
			initialFiles: [],
			citation: {},
			citationDialog: false,
			// Server-backed duplicate check. One entry per identifier the last
			// completed lookup answered for, keyed by identifier:
			// `{ value, match }` where `value` is the raw text that was checked
			// and `match` is the server's answer (or null for "no match").
			// Keeping the checked value alongside the answer is what makes the
			// synchronous validator honest - the moment the user edits a field,
			// its stored answer stops applying and the error clears until the
			// next lookup lands.
			identifierChecks: {},
			// The lookup could not be made. The form stays SAVEABLE (the server
			// guard is the real enforcement point); this only suppresses a
			// warning we no longer have grounds for.
			identifierCheckFailed: false,
			// uuid of the paper an "Attach to this project" request is in flight
			// for, so only that button shows a spinner.
			attaching: null,
			// DCTL-XXXX (DHELP-437): the keep-or-restart choice for attaching a
			// paper that already has extractions (AttachExistingPaperDialog).
			attachChoice: {
				open: false,
				match: null,
				preview: { extractions: [], blockedByQc: false },
				submitting: false,
			},
			ctgovDialog: false,
			ctgovPreview: {},
			nctId: this.defaultPaper ? this.defaultPaper.nct_id : null,
			showCitationDetails: false,
			dataEntryEnabled: true,
			doi: this.defaultPaper ? this.defaultPaper.doi : "",
			editMode: false,
			editTitle: "Edit paper",
			name: this.defaultPaper ? this.defaultPaper.name : "",
			newVersion: false,
			noIDs: false,
			orderingWarningDialog: false,
			pmid: this.defaultPaper ? this.defaultPaper.pmid : "",
			pmcid: this.defaultPaper ? this.defaultPaper.pmcid : "",
			priority: this.defaultPaper ? this.defaultPaper.priority : null,
			projects:
				this.defaultPaper && this.defaultPaper.projects
					? [...this.defaultPaper.projects]
					: this.lockedProject
						? [this.lockedProject]
						: [],
			removedFiles: [],
			projectDirty: false,
			subInvalid: true,
			submitting: false,
			title: "Create paper",
			url: this.defaultPaper ? this.defaultPaper.url : "",
			// A pinned project takes precedence over cached session projects.
			useSessionProjects: !this.lockedProject,
			uuid: this.defaultPaper ? this.defaultPaper.uuid : "",

			// expanded citation fields
			publicationTitle: this.defaultPaper
				? this.defaultPaper.publication_title
				: null,
			journalAbbreviation: this.defaultPaper
				? this.defaultPaper.journal_abbreviation
				: null,
			issn: this.defaultPaper ? this.defaultPaper.issn : null,
			volume: this.defaultPaper ? this.defaultPaper.volume : null,
			issue: this.defaultPaper ? this.defaultPaper.issue : null,
			pages: this.defaultPaper ? this.defaultPaper.pages : null,
			shortTitle: this.defaultPaper ? this.defaultPaper.short_title : null,
			itemType: this.defaultPaper ? this.defaultPaper.item_type : null,
			publicationDate: this.defaultPaper
				? this.defaultPaper.publication_date
				: null,
			abstractNote: this.defaultPaper ? this.defaultPaper.abstract_note : null,
			creators: this.defaultPaper ? this.defaultPaper.creators : null,
			citationTags: this.defaultPaper ? this.defaultPaper.citation_tags : null,
		};
	},

	computed: {
		...mapState(useMainStore, ["can"]),
		// Gates the therapeutic-area section: the /paper-characterizations endpoints
		// require manage_projects (administrators bypass via the store getter), so a
		// user without it never sees a section whose calls would 403.
		canManageProjects() {
			return this.can("manage_projects");
		},
		creatorsDisplay() {
			if (!this.creators || !this.creators.length) {
				return "";
			}
			return this.creators
				.filter((c) => c.creatorType === "author")
				.map((a) => a.name || `${a.firstName} ${a.lastName}`)
				.join(", ");
		},
		citationTagsDisplay() {
			if (!this.citationTags || !this.citationTags.length) {
				return "";
			}
			return this.citationTags.map((t) => t.tag).join(", ");
		},
		// NCT id detected in the Source URL field (a pasted ct.gov study link
		// or a bare id); drives the "Import from ClinicalTrials.gov" flow.
		detectedNctId() {
			const match = (this.url || "").match(/NCT\d{8}/i);
			return match ? match[0].toUpperCase() : null;
		},
		// Offers the import inline as soon as a study link is pasted, so the
		// curator need not find the button below. Gone once that study is
		// imported; a different NCT id pasted afterwards offers again.
		ctgovImportOffer() {
			if (!this.ctgovSupport || this.editMode || this.submitting) return null;
			if (!this.detectedNctId || this.detectedNctId === this.nctId) return null;
			return `${this.detectedNctId} is a ClinicalTrials.gov study.`;
		},
		urlMessages() {
			return this.ctgovImportOffer ? [this.ctgovImportOffer] : [];
		},
		// Everything the duplicate lookup sends, as one string, so a single
		// watcher covers all four identifiers and the name.
		identifierProbe() {
			return [this.doi, this.pmid, this.pmcid, this.nctId, this.name]
				.map((value) => value || "")
				.join("\u0000");
		},
		doiMatch() {
			return this.currentMatch("doi");
		},
		pmidMatch() {
			return this.currentMatch("pmid");
		},
		pmcidMatch() {
			return this.currentMatch("pmcid");
		},
		nctMatch() {
			return this.currentMatch("nctId");
		},
		titleMatch() {
			return this.currentMatch(TITLE_KEY);
		},
		doiMessages() {
			return this.outsideMessagesFor(this.doiMatch);
		},
		pmidMessages() {
			return this.outsideMessagesFor(this.pmidMatch);
		},
		pmcidMessages() {
			return this.outsideMessagesFor(this.pmcidMatch);
		},
		/**
		 * The paper-name warning, as the `messages` prop wants it.
		 *
		 * `messages`, never `error-messages`: this is a warning and the save
		 * stays open. The name field also carries `error-messages`, and Vuetify
		 * shows those INSTEAD of messages when both are present, so a genuine
		 * name error is never pushed off the field by this line.
		 */
		nameMessages() {
			const message = this.titleMessage();
			return message ? [message] : [];
		},
		// The NCT id has no field of its own, so its "outside your
		// organizations" note needs a home of its own too.
		nctOutsideMessage() {
			const [message] = this.outsideMessagesFor(this.nctMatch);
			return message || "";
		},
		// Every blocking match at once. Nothing renders from this - each field
		// speaks for itself through its own message slot - but it is the honest
		// answer to "is this form refusing a duplicate right now?".
		blockingMatches() {
			return IDENTIFIER_FIELDS.map(({ key, label }) => ({
				key,
				label,
				match: this.blockingMatch(key),
			})).filter((entry) => entry.match);
		},
		// "Attach to this project" only makes sense while CREATING a paper into
		// a pinned project. In edit mode the duplicate is some other paper, and
		// silently attaching it while the user edits this one would be a
		// different action than the one they asked for.
		canOfferAttach() {
			return Boolean(this.lockedProject) && !this.editMode;
		},
		nameErrors() {
			if (!this.v$.name.$dirty) {
				return [];
			}

			return this.v$.name.$errors.map((error) => {
				switch (error.$validator) {
					case "maxLength":
						return "Name is too long";
					default:
						return "Name is invalid";
				}
			});
		},
		priorityErrors() {
			if (!this.v$.name.$dirty) {
				return [];
			}

			return this.v$.priority.$errors.map((error) => {
				switch (error.$validator) {
					case "nonNumeric":
						return "Priority must be a number";
					case "negative":
						return "Priority must be non-negative";
					default:
						return "Priority is invalid";
				}
			});
		},
		urlErrors() {
			if (!this.v$.url.$dirty) {
				return [];
			}

			return this.v$.url.$errors.map((error) => {
				switch (error.$validator) {
					case "required":
						return "At least one of URL, DOI, PMID, or PMCID is required";
					case "maxLength":
						return "URL is too long";
					case "url":
						return "Invalid URL format";
					default:
						return "Invalid URL";
				}
			});
		},
		doiErrors() {
			if (!this.v$.doi.$dirty) {
				return [];
			}

			const errors = [];
			this.v$.doi.$errors.forEach((error) => {
				switch (error.$validator) {
					case "required":
						errors.push("At least one of URL, DOI, PMID, or PMCID is required");
						break;
					case "maxLength":
						errors.push("DOI is too long");
						break;
					case "unique":
						if (!this.newVersion) {
							errors.push(this.duplicateMessage("doi"));
						}
						break;
					default:
						errors.push("Invalid DOI");
						break;
				}
			});

			return errors;
		},
		pmidErrors() {
			if (!this.v$.pmid.$dirty) {
				return [];
			}

			const errors = [];
			this.v$.pmid.$errors.forEach((error) => {
				switch (error.$validator) {
					case "required":
						errors.push("At least one of URL, DOI, PMID, or PMCID is required");
						break;
					case "maxLength":
						errors.push("PMID is too long");
						break;
					case "unique":
						if (!this.newVersion) {
							errors.push(this.duplicateMessage("pmid"));
						}
						break;
					default:
						errors.push("Invalid PMID");
						break;
				}
			});

			return errors;
		},
		pmcidErrors() {
			if (!this.v$.pmcid.$dirty) {
				return [];
			}

			const errors = [];
			this.v$.pmcid.$errors.forEach((error) => {
				switch (error.$validator) {
					case "required":
						errors.push("At least one of URL, DOI, PMID, or PMCID is required");
						break;
					case "maxLength":
						errors.push("PMCID is too long");
						break;
					case "unique":
						if (!this.newVersion) {
							errors.push(this.duplicateMessage("pmcid"));
						}
						break;
					default:
						errors.push("Invalid PMCID");
						break;
				}
			});

			return errors;
		},
	},

	watch: {
		defaultPaper() {
			this.loadPaper();
		},
		identifierProbe() {
			this.queueIdentifierCheck();
		},
		"v$.$invalid": function (val) {
			if (val && this.v$.$anyDirty) {
				this.$emit("update:invalid", true);
			} else {
				this.$emit("update:invalid", false);
			}
		},
		files: {
			handler(val) {
				this.filesChanged = !_.isEqual(val, this.initialFiles);
			},
			deep: true,
		},
	},

	created() {
		// Per-instance, so two dialogs never share a pending timer.
		this.debouncedIdentifierCheck = _.debounce(
			this.runIdentifierCheck,
			IDENTIFIER_CHECK_DEBOUNCE_MS,
		);
		// Monotonic request id. A slower earlier lookup must not overwrite the
		// answer to a later one.
		this.identifierCheckSequence = 0;
	},

	mounted() {
		this.loadPaper();
		this.v$.$reset();
	},

	beforeUnmount() {
		this.debouncedIdentifierCheck.cancel();
	},

	methods: {
		...mapActions(useMainStore, ["showNotification"]),

		// ----- Duplicate identifier check ---------------------------------------

		/** The raw text currently in the field behind `key`. */
		identifierValue(key) {
			if (key === TITLE_KEY) {
				return this.name;
			}

			return key === "nctId" ? this.nctId : this[key];
		},

		/**
		 * The server's answer for `key`, but ONLY while the field still holds
		 * the text that answer was computed for. Anything else is stale, and a
		 * stale answer is worse than none: it would block a value the server was
		 * never asked about.
		 */
		currentMatch(key) {
			const check = this.identifierChecks[key];
			if (!check) {
				return null;
			}

			const value = this.identifierValue(key) || "";
			return check.value === value ? check.match : null;
		},

		/**
		 * The visible (save-refusing) match for one identifier, or null.
		 *
		 * Two things are deliberately NOT blocked:
		 *
		 * - a `newVersion` paper, which is allowed to repeat an identifier;
		 * - in edit mode, an identifier the user has not CHANGED. A paper that
		 *   already duplicates another one has to stay editable - production is
		 *   full of such pairs - and blocking here made the Update button
		 *   permanently grey for them, so the name, priority and projects could
		 *   not be touched either. The server's update guard has always allowed
		 *   this (it only refuses identifiers an edit actually changes); the
		 *   button simply did not mirror the rule, so the permissiveness was
		 *   unreachable. `unchanged` comes from the lookup rather than from
		 *   comparing strings here, so the two sides normalize identically.
		 *
		 * And the paper NAME never blocks, whatever the answers map holds for
		 * it. That is a set membership test rather than a `key !== "title"`,
		 * because the blocking set is `IDENTIFIER_FIELDS` and anything checked
		 * that is not in it has to stay out of the save gate by construction.
		 */
		blockingMatch(key) {
			if (!BLOCKING_KEYS.has(key)) {
				return null;
			}

			if (this.newVersion) {
				return null;
			}

			const match = this.currentMatch(key);
			if (!match || !match.visible || match.unchanged) {
				return null;
			}

			return match;
		},

		/**
		 * The line this field shows for its blocking duplicate, or null when it
		 * has none.
		 *
		 * "In this project" is tested FIRST, because it is the most specific
		 * thing that can be true and it is the reported case. `hasProjects` is
		 * only false for an orphaned paper, which only an administrator can
		 * match; anything other than an explicit false takes the common wording.
		 */
		duplicateMessage(key) {
			return this.messageFor(this.blockingMatch(key), DUPLICATE_MESSAGES);
		},

		/**
		 * Which of a copy set's three lines a match calls for, or null when
		 * there is no match. Shared by the identifier fields and the name field
		 * so the branch is defined once and the two sets stay parallel.
		 */
		messageFor(match, messages) {
			if (!match) {
				return null;
			}

			if (match.alreadyInProject && this.lockedProject) {
				return messages.inProject;
			}

			return match.hasProjects === false
				? messages.orphaned
				: messages.projected;
		},

		/**
		 * The paper-name match worth warning about, or null.
		 *
		 * Unlike `blockingMatch` this refuses nothing - it only decides whether
		 * to say anything. `unchanged` is suppressed for the same reason an
		 * identifier's is: in edit mode a name the user has not touched is not
		 * this edit's doing, and the server answers that question rather than
		 * the browser comparing strings. `newVersion` is the existing escape
		 * hatch - a new version of a paper is expected to repeat it.
		 *
		 * A title match is only ever returned for a paper the caller can SEE
		 * (the server omits the key otherwise), so `visible` is true by
		 * construction; the test is here so a hidden shape could never render a
		 * line that names a project.
		 */
		titleWarningMatch() {
			if (this.newVersion) {
				return null;
			}

			const match = this.currentMatch(TITLE_KEY);
			if (!match || !match.visible || match.unchanged) {
				return null;
			}

			return match;
		},

		/** The line the name field shows for a same-name paper, or null. */
		titleMessage() {
			return this.messageFor(this.titleWarningMatch(), TITLE_MESSAGES);
		},

		/**
		 * The name match a given MESSAGE is about, or null - the same
		 * copy-matching `duplicateForMessage` does, so the attach action rides
		 * the warning line and not "Name is too long".
		 */
		titleForMessage(message) {
			return message === this.titleMessage() ? this.titleWarningMatch() : null;
		},

		/**
		 * The blocking match a given MESSAGE is about, or null.
		 *
		 * The field's message slot runs once per message, and a field can be
		 * showing "DOI is too long" at the same time. Matching on the copy
		 * itself is what keeps the attach action and the paper-name tooltip
		 * attached to the duplicate line and off the others; both sides read the
		 * string from `DUPLICATE_MESSAGES`, so there is one definition of it.
		 */
		duplicateForMessage(key, message) {
			return message === this.duplicateMessage(key)
				? this.blockingMatch(key)
				: null;
		},

		/** The non-blocking note for a match outside the caller's orgs. */
		outsideMessagesFor(match) {
			if (this.newVersion || !match || match.visible) {
				return [];
			}

			return [OUTSIDE_ORGANIZATIONS_MESSAGE];
		},

		queueIdentifierCheck() {
			this.debouncedIdentifierCheck();
		},

		async runIdentifierCheck() {
			const probe = {
				doi: this.doi || "",
				pmid: this.pmid || "",
				pmcid: this.pmcid || "",
				nctId: this.nctId || "",
				// The paper name rides the same lookup and the same staleness
				// guard. A form with nothing but a name typed into it is still
				// worth asking about - that is the whole point of the warning.
				[TITLE_KEY]: this.name || "",
			};

			// Nothing to ask about.
			if (!Object.values(probe).some((value) => value.trim())) {
				this.identifierChecks = {};
				return;
			}

			this.identifierCheckSequence += 1;
			const sequence = this.identifierCheckSequence;

			try {
				const response = await this.$http.get("/papers/identifier-matches", {
					params: {
						...probe,
						// Edit mode: a paper is not its own duplicate. This
						// is what the old validator's `orig === value`
						// escape hatch did, moved to where the comparison
						// can be made against every paper rather than the
						// one page the table happened to be showing.
						excludePaperUuid: this.defaultPaper?.uuid || "",
						projectUuid: this.lockedProject?.uuid || "",
					},
				});

				// A later lookup already answered; drop this one.
				if (sequence !== this.identifierCheckSequence) {
					return;
				}

				const matches = response.data.matches || {};
				const checks = {};
				CHECKED_KEYS.forEach((key) => {
					checks[key] = {
						value: probe[key],
						match: matches[key] || null,
					};
				});
				this.identifierChecks = checks;
				this.identifierCheckFailed = false;
			} catch (error) {
				if (sequence !== this.identifierCheckSequence) {
					return;
				}

				// Fail OPEN. The server refuses a real duplicate on save, so
				// blocking the form on a lookup we could not make would strand
				// the user over an answer we do not have.
				this.identifierChecks = {};

				// Say it once per outage, not once per keystroke.
				if (!this.identifierCheckFailed) {
					this.identifierCheckFailed = true;
					console.error(error);
					this.showNotification({
						message:
							"Could not check whether this paper is already in Dactyl. You can still save it.",
						status: "error",
					});
				}
			}
		},

		/**
		 * Seed the duplicate state from a 409 the server raised on save. The
		 * user can beat the debounce by typing an identifier and pressing Add
		 * immediately; this turns the resulting refusal into the same field
		 * error and attach shortcut the lookup would have produced.
		 */
		applyServerDuplicate(duplicate) {
			if (!duplicate || !duplicate.identifier) {
				return;
			}

			const key = duplicate.identifier;
			const value = this.identifierValue(key) || "";
			this.identifierChecks = {
				...this.identifierChecks,
				[key]: {
					value,
					match: {
						visible: true,
						paperUuid: duplicate.paperUuid,
						name: duplicate.name,
						alreadyInProject: false,
					},
				},
			};
			this.v$.$touch();
		},

		/**
		 * Attach the EXISTING paper to the pinned project instead of creating a
		 * duplicate. A dedicated single attach, not the diff-sync
		 * PUT /papers/:uuid/projects, which would detach every other project.
		 *
		 * DCTL-XXXX (DHELP-437): a paper is one record shared by all its
		 * projects, so attaching brings its extractions (and the board stage
		 * they produce) along. Ask first: the preview lists them, and a paper
		 * that has any opens AttachExistingPaperDialog to choose between
		 * keeping them and restarting extraction from scratch. A paper with no
		 * live extractions attaches straight away, as before.
		 */
		async attachExisting(match) {
			if (!this.lockedProject) {
				return;
			}

			this.attaching = match.paperUuid;

			let preview;
			try {
				const response = await this.$http.get(
					`/papers/${match.paperUuid}/attach-project`,
					{ params: { projectUuid: this.lockedProject.uuid } },
				);
				preview = response.data;
			} catch (error) {
				this.attaching = null;
				this.notifyAttachFailed(error);
				return;
			}

			if (!preview?.extractions?.length) {
				await this.postAttach(match, false);
				return;
			}

			this.attaching = null;
			this.attachChoice = {
				open: true,
				match,
				preview,
				submitting: false,
			};
		},

		chooseAttach(restartExtraction) {
			this.attachChoice.submitting = restartExtraction ? "restart" : "keep";
			this.postAttach(this.attachChoice.match, restartExtraction);
		},

		async postAttach(match, restartExtraction) {
			this.attaching = match.paperUuid;
			try {
				const response = await this.$http.post(
					`/papers/${match.paperUuid}/attach-project`,
					// The keep path is the original request, unchanged; only a
					// restart carries the flag (the server reads a literal true).
					restartExtraction
						? { projectUuid: this.lockedProject.uuid, restartExtraction: true }
						: { projectUuid: this.lockedProject.uuid },
				);
				this.attachChoice.open = false;
				if (restartExtraction) {
					const retired = response.data?.retiredCount ?? 0;
					this.showNotification({
						message: `${response.data?.message || "Attached"}. Extraction restarted: ${retired} ${retired === 1 ? "extraction" : "extractions"} retired.`,
						status: "success",
					});
				} else {
					this.showNotification(response.data);
				}
				this.$emit("refresh");
			} catch (error) {
				// A refused restart (a QC record appeared since the preview)
				// attaches nothing: the server rolls both back. The choice
				// dialog stays open so "keep" is still one click away.
				this.notifyAttachFailed(error);
			} finally {
				this.attaching = null;
				this.attachChoice.submitting = false;
			}
		},

		notifyAttachFailed(error) {
			if (error.response?.data?.message) {
				this.showNotification(error.response.data);
			} else {
				console.error(error);
				this.showNotification({
					message: "The paper could not be attached to this project",
					status: "error",
				});
			}
		},

		clear() {
			// clear dirty flags
			this.v$.$reset();

			// clear the duplicate answers; they belong to the values just wiped
			this.identifierChecks = {};
			this.identifierCheckFailed = false;

			// clear citation
			this.citation = {};

			if (_.isEmpty(this.defaultPaper)) {
				this.name = "";
				this.url = "";
				this.doi = "";
				this.pmid = "";
				this.pmcid = "";
				this.nctId = null;
				this.ctgovPreview = {};
				this.priority = null;
				this.uuid = "";

				this.publicationTitle = null;
				this.journalAbbreviation = null;
				this.issn = null;
				this.volume = null;
				this.issue = null;
				this.pages = null;
				this.shortTitle = null;
				this.itemType = null;
				this.publicationDate = null;
				this.abstractNote = null;
				this.creators = null;
				this.citationTags = null;

				this.filesChanged = false;

				this.files = [];
				// Preserve the pinned project across a Clear in add-from-project mode.
				this.projects = this.lockedProject ? [this.lockedProject] : [];

				this.useSessionProjects = !this.lockedProject;

				this.editMode = false;
				this.newVersion = false;
				this.dataEntryEnabled = true;

				setTimeout(() => {
					if (this.$refs.projectSelect) {
						this.$refs.projectSelect.clear();
					}
				}, 250);
			} else {
				this.loadPaper();
			}
		},
		cancel() {
			this.$emit("cancelPaperEdit");
		},
		add() {
			this.save("add");
		},
		update() {
			this.save("update");
		},
		save(mode, overrideOrderingWarning = false) {
			if (mode === "update" && !overrideOrderingWarning) {
				// Position in the array IS the ordering, on both sides: the API
				// returns files already sorted (File.applyPaperOrdering) and the
				// uploader reorders the array itself. So this check can ask its
				// question by index and never has to WRITE anything.
				//
				// It used to stamp `file.ordering = index + 1` across
				// `this.files` first. That array is a SHALLOW copy of
				// `defaultPaper.files` (loadPaper), so those are the PARENT's
				// file objects: when the check then refused the save, the
				// rejected ordering had already been written into the parent's
				// in-memory paper, and reopening the dialog without a page
				// refresh showed the refused order in the "Existing ordering"
				// column as though it were saved. The real save path still
				// assigns `ordering`, in updatePaperFilesRequest, where the write
				// is actually going somewhere.
				const initialOrderingMap = new Map();
				this.initialFiles.forEach((file, index) => {
					initialOrderingMap.set(file.uuid, index + 1);
				});

				// check for ordering changes
				const orderingChanged = _.some(
					this.files,
					(file, index) =>
						file.uuid && initialOrderingMap.get(file.uuid) !== index + 1,
				);

				const hasSdes = this.defaultPaper?.dataEntries?.length;

				if ((this.removedFiles.length || orderingChanged) && hasSdes) {
					this.orderingWarningDialog = true;
					return;
				}
			}

			this.saveRequest(mode)
				.then((response) => {
					// display success
					this.showNotification(response.data);
					// update paper table
					this.$emit("refresh");
				})
				.catch((error) => {
					// set all to dirty
					this.v$.$touch();

					// display error
					if (error.response) {
						if (error.response.data.message) {
							this.showNotification(error.response.data);
						} else {
							this.showNotification(error.response.data.error);
						}
					}
				});
		},
		saveRequest(mode) {
			if (!this.v$.$invalid) {
				this.submitting = "secondary";

				// create or select project
				return this.$refs.projectSelect
					.select()
					.then((projects) => {
						const dispatch = {
							add: this.addPaperRequest,
							update: this.updatePaperRequest,
						};

						return dispatch[mode]()
							.then((response) => {
								return Promise.all([
									this.updatePaperProjectsRequest(
										response.data.paper,
										projects,
									),
									this.updatePaperFilesRequest(response.data.paper),
								]).then(() => response);
							})
							.catch((error) => {
								// display error
								if (error.response) {
									// The server refused a duplicate the debounced
									// lookup had not answered for yet. Show it the
									// same way the lookup would have.
									if (error.response.status === 409) {
										this.applyServerDuplicate(error.response.data.duplicate);
									}

									if (error.response.data.message) {
										this.showNotification(error.response.data);
									} else {
										this.showNotification(error.response.data.error);
									}
								}
							});
					})
					.catch((error) => {
						// set dirty flags
						this.v$.$touch();

						// display error
						if (error && error.response) {
							if (error.response.data.message) {
								this.showNotification(error.response.data);
							} else {
								this.showNotification(error.response.data.error);
							}
						}
					})
					.finally(() => {
						this.submitting = false;
					});
			} else {
				return Promise.reject();
			}
		},
		updatePaperProjectsRequest(paper, projects) {
			return this.$http.put(`/papers/${paper.uuid}/projects`, {
				projectUUIDs: projects.map((project) => project.uuid),
			});
		},
		updatePaperFilesRequest(paper) {
			// create form data for file uploads
			const formData = new FormData();

			// load all new files into form data
			this.files.forEach((file, index) => {
				file.ordering = index + 1;
				if (!file.uuid) {
					formData.append(`files[${index}]`, file);
				}
				formData.append(`allFiles[${index}]`, JSON.stringify(file));
			});

			// only remove files that existed in the server (files with uuids)
			this.removedFiles.forEach((file, index) => {
				if (file.uuid) {
					formData.append(`removeFileUUIDs[${index}]`, file.uuid);
				}
			});

			return this.$http.post(`/papers/${paper.uuid}/files`, formData);
		},
		buildPaperPayload() {
			return {
				citation: JSON.stringify(this.citation),
				citation_format: _.isEmpty(this.citation) ? undefined : citationFormat,
				doi: this.doi,
				// ClinicalTrials.gov import stamp (add flow only; the update
				// endpoint ignores it). The backend fetches and attaches the
				// registry JSON when this is set.
				nct_id: this.nctId || undefined,
				name: this.name.trim(),
				pmid: this.pmid,
				pmcid: this.pmcid,
				priority: this.priority,
				source_url: this.url,
				status: this.dataEntryEnabled ? "DATA_ENTRY" : "NEW",
				publication_title: this.publicationTitle,
				journal_abbreviation: this.journalAbbreviation,
				issn: this.issn,
				volume: this.volume,
				issue: this.issue,
				pages: this.pages,
				short_title: this.shortTitle,
				item_type: this.itemType,
				publication_date: this.publicationDate,
				abstract_note: this.abstractNote,
				creators: this.creators,
				citation_tags: this.citationTags,
			};
		},
		addPaperRequest() {
			return this.$http.post("/papers", this.buildPaperPayload());
		},
		updatePaperRequest() {
			return this.$http.put(`/papers/${this.uuid}`, this.buildPaperPayload());
		},
		closeOrderingWarningDialog() {
			this.orderingWarningDialog = false;
		},
		loadPaper() {
			if (!_.isEmpty(this.defaultPaper)) {
				this.name = this.defaultPaper.name;
				this.url = this.defaultPaper.source_url;
				this.doi = this.defaultPaper.doi;
				this.pmid = this.defaultPaper.pmid;
				this.pmcid = this.defaultPaper.pmcid;
				this.nctId = this.defaultPaper.nct_id;
				this.priority = this.defaultPaper.priority;
				this.uuid = this.defaultPaper.uuid;

				this.publicationTitle = this.defaultPaper.publication_title;
				this.journalAbbreviation = this.defaultPaper.journal_abbreviation;
				this.issn = this.defaultPaper.issn;
				this.volume = this.defaultPaper.volume;
				this.issue = this.defaultPaper.issue;
				this.pages = this.defaultPaper.pages;
				this.shortTitle = this.defaultPaper.short_title;
				this.itemType = this.defaultPaper.item_type;
				this.publicationDate = this.defaultPaper.publication_date;
				this.abstractNote = this.defaultPaper.abstract_note;
				this.creators = this.defaultPaper.creators;
				this.citationTags = this.defaultPaper.citation_tags;

				this.dataEntryEnabled =
					this.defaultPaper.status.key === "NEW" ? false : true;

				if (_.isEmpty(this.initialFiles)) {
					this.initialFiles = _.cloneDeep(this.defaultPaper.files);
					this.files = [...this.defaultPaper.files];
				} else {
					this.files = _.cloneDeep(this.initialFiles);
				}

				this.projects = [...this.defaultPaper.projects];

				// do not load session projects if existing paper does not have
				// associated projects
				if (!this.projects.length) {
					this.useSessionProjects = false;
				}

				this.editMode = true;

				// (for now) always start loaded papers with empty citations
				this.citation = {};

				// default no IDs toggle to true if loading a paper without any IDs
				if (!this.url && !this.doi && !this.pmid && !this.pmcid) {
					this.noIDs = true;
				} else {
					this.noIDs = this.defaultPaper.noIDs;
				}
			} else {
				this.clear();
			}
		},
		fileRemoved(file) {
			this.removedFiles.push(file);
			this.filesChanged = true;
		},
		async getCitation() {
			this.submitting = "secondary";

			const queries = [];

			// attempt to fetch PMID from PubMed
			if (!this.pmid) {
				const pmid = await this.fetchPMID();

				if (pmid) {
					queries.push(`PMID ${pmid}`);
				}
			}

			// if PubMed PMID not found, fall back on user input PMID
			if (this.pmid) {
				queries.push(`PMID ${this.pmid}`);
			}
			if (this.pmcid) {
				queries.push(this.pmcid);
			}
			if (this.doi) {
				queries.push(this.doi);
			}
			if (this.url) {
				queries.push(this.url);
			}

			if (queries.length) {
				this.fetchCitation(queries)
					.then((response) => {
						this.citation = response.data[0];
					})
					.catch((error) => {
						// display error
						if (error.response.status === 404) {
							this.showNotification({
								message: "Citation not found, please try again",
								status: error.response.status,
							});
						}

						// close the citation confirmation dialog
						this.citationDialog = false;
					})
					.finally(() => {
						this.submitting = false;
					});
			}
		},
		fetchPMID() {
			const queries = [];

			// pubmed search does not support searching by URL
			if (this.doi) {
				queries.push(`${this.doi}[DOI]`);
			}
			if (this.pmcid) {
				queries.push(`${this.pmcid}[ALL]`);
			}

			// fetch PMID from pubmed
			return new Promise((resolve) => {
				this.$http
					.get("", {
						baseURL: entrezURL,
						params: {
							// to be updated with actual dactyldata.com address
							email: "chow.eric.william@gmail.com",
							retmode: "json",
							term: queries, // array to feed into custom serializer
							tool: "dactyl",
						},
						paramsSerializer: (params) => {
							// custom serializer needed to prevent "+AND+" between terms from being encoded
							return Object.keys(params).reduce((prev, curr) => {
								if (curr === "term") {
									const term = params[curr]
										.map((item) => encodeURIComponent(item))
										.join("+AND+");
									return prev + `&${curr}=${term}`;
								} else {
									return prev + `&${curr}=${encodeURIComponent(params[curr])}`;
								}
							}, "");
						},
						withCredentials: false,
					})
					.then((response) => {
						if (response.data.esearchresult.count > 0) {
							resolve(response.data.esearchresult.idlist[0]);
						} else {
							resolve();
						}
					})
					.catch((error) => {
						// log the error in the console and silently fail
						console.error(error);
						resolve();
					});
			});
		},
		fetchCitation(queries) {
			let index = 0;

			// fetch citation from citoid
			return new Promise((resolve, reject) => {
				const next = () => {
					if (index < queries.length) {
						this.$http
							.get(`${encodeURIComponent(queries[index])}`, {
								baseURL: citoidURL,
								headers: {
									accept: "application/json; charset=utf-8;",
								},
								withCredentials: false,
							})
							.then((response) => {
								// found citation, stop iterating
								resolve(response);
							})
							.catch((error) => {
								if (queries.length - index > 1) {
									// increment index
									index++;

									// make the next request
									next();
								} else {
									// reached the end of queries
									reject(error);
								}
							});
					} else {
						reject();
					}
				};

				// start iteration
				next();
			});
		},
		confirmCitation() {
			const extras = {};

			// parse out pmid and pmcid
			if (this.citation.extra) {
				this.citation.extra.split("\n").forEach((line) => {
					const data = line.split(": ");
					extras[data[0]] = data[1];
				});
			}

			// pmid and pmcid are not included as their own fields from zotero
			this.pmid = extras.PMID ? extras.PMID : this.pmid;
			this.pmcid = extras.PMCID ? extras.PMCID : this.pmcid;

			// set the values from the citation
			this.doi = this.citation.DOI ? this.citation.DOI : this.doi;
			this.url = this.citation.url ? this.citation.url : this.url;

			// special case for handling title (zotero specific)
			const lastName = !_.isEmpty(this.citation.creators)
				? this.citation.creators[0].lastName
				: "<Missing Last Name>";

			const date = !_.isEmpty(this.citation.date)
				? this.citation.date.slice(0, 4)
				: "<Missing Date>";
			this.name = `${lastName}, ${date}; ${this.citation.title}`;

			// set expanded citation fields
			this.publicationTitle =
				this.citation.publicationTitle || this.publicationTitle;
			this.journalAbbreviation =
				this.citation.journalAbbreviation || this.journalAbbreviation;
			this.issn = this.citation.ISSN || this.issn;
			this.volume = this.citation.volume || this.volume;
			this.issue = this.citation.issue || this.issue;
			this.pages = this.citation.pages || this.pages;
			this.shortTitle = this.citation.shortTitle || this.shortTitle;
			this.itemType = this.citation.itemType || this.itemType;
			this.publicationDate = this.citation.date || this.publicationDate;
			this.abstractNote = this.citation.abstractNote || this.abstractNote;
			this.creators = this.citation.creators || this.creators;
			this.citationTags = this.citation.tags || this.citationTags;

			// set all to dirty
			this.v$.$touch();

			// close the citation confirmation dialog
			this.citationDialog = false;
		},
		cancelCitation() {
			// close the citation confirmation dialog
			this.citationDialog = false;

			// clear the fetched citation
			this.citation = {};
		},
		async getCtgovPreview() {
			if (!this.detectedNctId) {
				this.showNotification({
					message:
						"Paste a ClinicalTrials.gov study link (or NCT id) into the Source URL field first",
					status: "error",
				});
				return;
			}

			this.submitting = "secondary";
			this.ctgovPreview = {};
			this.ctgovDialog = true;

			try {
				const response = await this.$http.get(
					`/papers/ctgov/${this.detectedNctId}`,
				);
				this.ctgovPreview = response.data.preview;
			} catch (error) {
				this.ctgovDialog = false;
				if (error.response?.data?.message) {
					this.showNotification(error.response.data);
				} else {
					this.showNotification({
						message: "ClinicalTrials.gov could not be reached",
						status: "error",
					});
				}
			} finally {
				this.submitting = false;
			}
		},
		confirmCtgovImport() {
			const preview = this.ctgovPreview;

			// Stamp the import; POST /papers fetches and attaches the registry
			// JSON server-side when nct_id is present.
			this.nctId = preview.nctId;

			// Prefill the form; everything stays editable before Add.
			this.name = preview.briefTitle
				? `${preview.nctId}: ${preview.briefTitle}`
				: preview.nctId;
			this.url = `https://clinicaltrials.gov/study/${preview.nctId}`;

			this.v$.name.$touch();
			this.v$.url.$touch();

			this.ctgovDialog = false;
		},
		cancelCtgovImport() {
			this.ctgovDialog = false;
			this.ctgovPreview = {};
		},
	},

	validations() {
		if (this.newVersion) {
			return {
				name: {
					maxLength: maxLength(2048),
				},
				url: {
					url,
					maxLength: maxLength(2048),
					required: requiredUnless(function () {
						return this.noIDs || this.doi || this.pmid || this.pmcid;
					}),
				},
				doi: {
					maxLength: maxLength(255),
					required: requiredUnless(function () {
						return this.noIDs || this.pmid || this.pmcid || this.url;
					}),
				},
				newVersion: {},
				noIDs: {},
				pmid: {
					maxLength: maxLength(255),
					required: requiredUnless(function () {
						return this.noIDs || this.doi || this.pmcid || this.url;
					}),
				},
				pmcid: {
					maxLength: maxLength(255),
					required: requiredUnless(function () {
						return this.noIDs || this.doi || this.pmid || this.url;
					}),
				},
				priority: {
					nonNumeric: (value) => !isNaN(value),
					negative: (value) => value >= 0,
				},
				dataEntryEnabled: {},
				publicationTitle: {},
				shortTitle: {},
				journalAbbreviation: {},
				issn: {},
				volume: {},
				issue: {},
				pages: {},
				itemType: {},
				publicationDate: {},
				abstractNote: {},
				subInvalid: {
					isFalse,
				},
			};
		} else {
			return {
				name: {
					maxLength: maxLength(2048),
				},
				url: {
					url,
					maxLength: maxLength(2048),
					required: requiredUnless(function () {
						return this.noIDs || this.doi || this.pmid || this.pmcid;
					}),
				},
				doi: {
					maxLength: maxLength(255),
					required: requiredUnless(function () {
						return this.noIDs || this.pmid || this.pmcid || this.url;
					}),
					unique: () => !this.blockingMatch("doi"),
				},
				newVersion: {},
				noIDs: {},
				pmid: {
					maxLength: maxLength(255),
					required: requiredUnless(function () {
						return this.noIDs || this.doi || this.pmcid || this.url;
					}),
					unique: () => !this.blockingMatch("pmid"),
				},
				pmcid: {
					maxLength: maxLength(255),
					required: requiredUnless(function () {
						return this.noIDs || this.doi || this.pmid || this.url;
					}),
					unique: () => !this.blockingMatch("pmcid"),
				},
				priority: {
					nonNumeric: (value) => !isNaN(value),
					negative: (value) => value >= 0,
				},
				dataEntryEnabled: {},
				publicationTitle: {},
				shortTitle: {},
				journalAbbreviation: {},
				issn: {},
				volume: {},
				issue: {},
				pages: {},
				itemType: {},
				publicationDate: {},
				abstractNote: {},
				subInvalid: {
					isFalse,
				},
			};
		}
	},
};
</script>

<style scoped lang="scss">
// The duplicate warning lives inside the field's OWN message slot, which
// Vuetify reserves whether or not there is a message (`min-height: 22px`), so
// on the full-width DOI field it costs the dialog no height at all. That is why
// it is here and not in a block of its own beneath the field.
//
// `flex-wrap: wrap` is deliberate. On the half-width PMID/PMCID fields the
// explanatory copy plus the action is wider than the column, and the choice is
// between wrapping the action onto a second line and ellipsizing the words that
// explain WHY the action is offered. Losing the words defeats the copy, so it
// wraps: one extra line, measured, on those two fields only.
.duplicate-inline {
	flex-wrap: wrap;
}

// The action never breaks mid-phrase and is the last thing to be given up. It
// takes its colour from the message it sits in (red for an error), so there is
// no colour of its own to source from constants.
.duplicate-inline__action {
	color: inherit;
	cursor: pointer;
	flex-shrink: 0;
	font-weight: 600;
	text-decoration: underline;
	white-space: nowrap;
}

.citation-details-toggle:hover {
	background-color: rgb(var(--v-theme-primary-tint));
}
</style>
